import { SessionModel } from './session.model';
import { codeVerifierStorageItemKey, localStorageSignOutKey, redirectUriStorageItemKey, tokenStorageItemKey } from './auth.consts';
import type { ConfigSettings } from '../App/services/mx-config-service';
import { generateCodeChallenge } from './authorizationCodeWithPkce';

export class AuthHelper {
	constructor(private readonly authConfig: ConfigSettings['authentication']) {}

	public createSessionFromToken(token: string) {
		const activeSession = new SessionModel(token);

		localStorage.removeItem(codeVerifierStorageItemKey);

		if (this.authConfig.oauth2.persistent) {
			try {
				const currentToken = localStorage.getItem(tokenStorageItemKey);
				activeSession.restored = currentToken === token;
				if (!activeSession.restored) {
					localStorage.setItem(tokenStorageItemKey, token);
				}
			} catch (error) {
				console.warn('Could not store session. You will need to login again after reloading the page.', error);
			}
		}

		activeSession.onDestroyed(() => {
			localStorage.removeItem(tokenStorageItemKey);
			localStorage.removeItem(codeVerifierStorageItemKey);
		});

		return activeSession;
	}

	public startAuthorizeWorkflow(orgUnitId?: string, storeCurrentUri = false) {
		const authUrl = new URL(this.authConfig.oauth2.endpoints.authorize);

		const targetLocation = this.getTargetLocation();
		authUrl.searchParams.set('redirect_uri', targetLocation);

		const passThrough = this.authConfig.passThrough;
		if (passThrough && targetLocation && targetLocation.indexOf(passThrough, targetLocation.length - passThrough.length) > -1) {
			authUrl.searchParams.set(passThrough, 'true');
		}

		const isSignOut = localStorage.getItem(localStorageSignOutKey);
		if (isSignOut) {
			localStorage.removeItem(localStorageSignOutKey);
			authUrl.searchParams.set('redirect_to_signout', 'true');
		}

		// When the redirect uri is fixed to a specific value because the used STS does not support deep
		// linking we cache the original target and use the fixed redirect uri.
		const authCode = this.getAuthorizationCodeFromUrl();
		if (storeCurrentUri && !authCode && (!localStorage.getItem(redirectUriStorageItemKey) || targetLocation !== this.authConfig.oauth2.redirect_uri)) {
			localStorage.setItem(redirectUriStorageItemKey, targetLocation);
		}
		authUrl.searchParams.set('redirect_uri', this.authConfig.oauth2.redirect_uri);

		authUrl.searchParams.set('client_id', this.authConfig.oauth2.clientid);
		authUrl.searchParams.set('scope', this.authConfig.oauth2.scope);
		authUrl.searchParams.set('response_type', 'token');

		if (orgUnitId) {
			authUrl.searchParams.set('ou', orgUnitId);
		}

		const isAuthCodeResponse = /^.*?code=.+&state=[^&]*$/.test(targetLocation);
		if (!isAuthCodeResponse && this.authConfig.oauth2 && !this.authConfig.oauth2.auth_with_implicit) {
			const codeChallenge = generateCodeChallenge();
			localStorage.setItem(codeVerifierStorageItemKey, codeChallenge.code_verifier);

			authUrl.searchParams.set('response_type', 'code');
			authUrl.searchParams.set('code_challenge', codeChallenge.code_challenge);
			authUrl.searchParams.set('code_challenge_method', codeChallenge.code_challenge_method);
		}

		window.location.href = authUrl.toString();
	}

	public startLogoutWorkflow(session?: SessionModel, storeCurrentUri = false, orgUnitId?: string) {
		// Save current URL to be able to restore it on next reLogin
		const authCode = this.getAuthorizationCodeFromUrl();
		if (!authCode && storeCurrentUri) {
			localStorage.setItem(redirectUriStorageItemKey, window.location.origin + window.location.pathname + window.location.search);
		}

		const redirectUri = new URL(this.authConfig.oauth2.endpoints.signout);
		redirectUri.searchParams.set('redirect_to_signout', 'true');
		if (orgUnitId) {
			redirectUri.searchParams.set('ou', orgUnitId);
		}
		const stsLogOutUrl = new URL(this.authConfig.oauth2.endpoints.stsLogout);
		stsLogOutUrl.searchParams.set('redirect_uri', redirectUri.toString());

		if (session) {
			session.destroy();
		}

		window.location.href = stsLogOutUrl.toString();
	}

	public getTargetLocation() {
		return window.location.href;
	}

	private getAuthorizationCodeFromUrl() {
		const searchParams = new URLSearchParams(window.location.search);
		return searchParams.get('code');
	}

	private getAuthorizationStateFromUrl() {
		const searchParams = new URLSearchParams(window.location.search);
		return searchParams.get('state');
	}

	private getAccessTokenFromHash() {
		const searchParams = new URLSearchParams(window.location.hash);
		return searchParams.get('access_token') || searchParams.get('#access_token');
	}

	public areReturningFromExternalAuthorization() {
		const accessToken = this.getAccessTokenFromHash();
		return accessToken !== null && accessToken !== undefined;
	}

	public areReturningFromExternalAuthorizationWithPKCE() {
		return !this.authConfig.oauth2.auth_with_implicit && window.location.href.indexOf('externalAuth') === -1 && !!this.getAuthorizationCodeFromUrl();
	}

	private getSymbolPositionOrLength(url: string, symbol: string) {
		const symbolPosition = url.indexOf(symbol);
		return symbolPosition === -1 ? url.length : symbolPosition;
	}

	private finishPendingAuthorizeWorkflow() {
		/*
		Check if we come back from a successful authorization (with token)
		and have an additional Url-Path stored in the local storage
		If so, insert the additional path between the (if configured) redirect Uri and access token
		go to this new location
		 */
		let cachedRedirectUri = localStorage.getItem(redirectUriStorageItemKey);
		let currentURL = window.location.href.toString();
		const currentUrlWithoutHash = currentURL.substring(0, this.getSymbolPositionOrLength(currentURL, '#'));

		if (!currentURL.startsWith(this.authConfig.oauth2.redirect_uri)) {
			currentURL = this.authConfig.oauth2.redirect_uri;
		}

		// If current URL is specified (not the same as default)
		// then it has higher priority than stored in localStorage (which was saved on LogOut for restoring last location)
		// because user uses this URL explicitly in address bar.
		if (cachedRedirectUri && currentUrlWithoutHash !== this.authConfig.oauth2.redirect_uri) {
			localStorage.removeItem(redirectUriStorageItemKey);
			cachedRedirectUri = null;
		}

		if (cachedRedirectUri) {
			const redirectUriEndIndex = this.authConfig.oauth2.redirect_uri.length;
			let params = currentURL.slice(redirectUriEndIndex);

			// For the case when '/' in the end of url does matter.
			// It's need to redirect to exactly the same `currentURL` with or without `/` in the end.
			if (params.indexOf('/?') === 0) {
				params = params.replace('/?', '?');
			}

			// for the case if `cachedRedirectUri` has its own GET params
			if (cachedRedirectUri.indexOf('?') !== -1) {
				// Check if `params` are GET-params or not (like: "/").
				// If not then set to "" not to break GET-params of `cachedRedirectUri`.
				params = params.indexOf('?') === 0 ? params.replace('?', '&') : '';
			}

			const newUrl = [cachedRedirectUri, params].join('');
			localStorage.removeItem(redirectUriStorageItemKey);

			window.location.href = newUrl;
		}

		if (!this.areReturningFromExternalAuthorization()) {
			throw new Error('M42ACSSdkNg: No authorization request is pending, aborting');
		}

		return this.getAccessTokenFromHash();
	}

	/*
	 * This method exchanges the authorization code for an access token
	 * after success authentication with Proof Key for Code Exchange (PKCE)
	 *
	 */
	private async exchangeAuthorizationCodeForAccessToken() {
		const code_verifier = localStorage.getItem(codeVerifierStorageItemKey);
		if (!code_verifier) {
			throw new Error('Code verifier required');
		}

		const authCode = this.getAuthorizationCodeFromUrl();
		if (!authCode) {
			throw new Error('Authorization Code required');
		}

		const authState = this.getAuthorizationStateFromUrl();
		if (!authState) {
			throw new Error('State required');
		}

		const response = await fetch(this.authConfig.oauth2.endpoints.token, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: new URLSearchParams({
				grant_type: 'authorization_code',
				code: authCode,
				code_verifier: code_verifier,
				state: authState,
				client_id: this.authConfig.oauth2.clientid,
				scope: this.authConfig.oauth2.scope,
				redirect_uri: this.authConfig.oauth2.redirect_uri,
			}),
		});

		if (!response.ok) {
			let errorMessage = 'Error on exchanging the authorization code for an access token';
			try {
				const result = await response.json();
				if (result?.error) {
					errorMessage = result?.error;
				}
			} catch (e) {
				//do nothing
			}
			throw new Error(errorMessage);
		}
		return await response.json();
	}

	private async pkceFlow() {
		const authCodeWithPKCE = this.areReturningFromExternalAuthorizationWithPKCE();
		if (authCodeWithPKCE) {
			const response = await this.exchangeAuthorizationCodeForAccessToken();
			if (response && response.access_token) {
				const session = this.createSessionFromToken(response.access_token);

				const cachedRedirectUri = localStorage.getItem(redirectUriStorageItemKey);
				if (cachedRedirectUri) {
					localStorage.removeItem(redirectUriStorageItemKey);
					window.location.href = cachedRedirectUri;
				}

				return session;
			}
		}
		return;
	}

	private standardAuthFlow() {
		const requestCurrentlyFinishingPendingAuth = this.areReturningFromExternalAuthorization();
		if (requestCurrentlyFinishingPendingAuth) {
			const token = this.finishPendingAuthorizeWorkflow();
			if (token) {
				return this.createSessionFromToken(token);
			}
		}
		return;
	}

	private directTokenFlow(directToken?: string) {
		return directToken && this.createSessionFromToken(directToken);
	}

	private persistentTokenFlow() {
		if (this.authConfig.oauth2.persistent /*&& !activeSession*/) {
			const tokenFromStorage = localStorage.getItem(tokenStorageItemKey);
			if (tokenFromStorage) {
				return this.createSessionFromToken(tokenFromStorage);
			}
		}
		return;
	}

	public async makeSessionAvailable(directToken?: string): Promise<SessionModel | undefined> {
		return (await this.pkceFlow()) || this.standardAuthFlow() || this.directTokenFlow(directToken) || this.persistentTokenFlow();
	}
}
